import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Compass, Heart, HeartHandshake, Layers, MapPin,
  MessagesSquare, Mountain, Send, Star, Sun, Users, Wallet,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SectionHeading } from "../components/SectionHeading";
import { DestinationCard } from "../components/DestinationCard";
import { EnquiryForm } from "../components/EnquiryForm";
import {
  DESTINATIONS, IMAGES, MARQUEE_ITEMS, REVIEWS, SERVICES,
  SITE, TRAVEL_STYLES, waLink, WA_DEFAULT_MSG,
} from "../config/site";

const HERO_LINES = ["Your Next", "Adventure", "Starts Here"];

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 200]);

  return (
    <section data-testid="hero-section" className="hero-grain relative flex min-h-[100svh] items-center overflow-hidden bg-ocean">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={IMAGES.hero} alt="Aerial view of a tropical beach destination" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ocean/70 via-ocean/40 to-ocean/85" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 pb-44 pt-32 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="text-xs font-semibold uppercase tracking-[0.3em] text-sand"
        >
          Arora Travel Online · Rohini, New Delhi
        </motion.p>

        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {HERO_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 1 ? <span className="text-gold">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/80"
        >
          Discover destinations and travel options tailored to your plans,
          preferences and budget.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <Link
            to="/destinations"
            data-testid="hero-explore-destinations-button"
            className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-ocean transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
          >
            Explore Destinations <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/plan-your-trip"
            data-testid="hero-plan-trip-button"
            className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-coral/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B01A23]"
          >
            Plan My Trip <Send className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="absolute inset-x-4 bottom-6 z-10 mx-auto max-w-3xl"
      >
        <div className="flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-md">
          {["Flights", "Holiday Packages", "Cruises", "Visa Assistance", "Travel Insurance"].map((chip) => (
            <Link
              key={chip}
              to="/services"
              className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-medium text-white/85 transition-colors hover:border-gold hover:text-gold"
            >
              {chip}
            </Link>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const BENTO_SPANS = [
  "md:col-span-4 md:row-span-2", "md:col-span-2", "md:col-span-2",
  "md:col-span-2", "md:col-span-3", "md:col-span-3",
  "md:col-span-4", "md:col-span-2",
];
const BENTO_STYLES = [
  "bg-ocean text-white", "bg-white", "bg-sand",
  "bg-turquoise/10", "bg-white", "bg-white",
  "bg-sand", "bg-turquoise/10",
];

const ServicesBento = () => (
  <section id="services" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
    <div className="flex flex-wrap items-end justify-between gap-6">
      <SectionHeading
        eyebrow="What We Do"
        title="Our Travel Services"
        text="Eight ways we can help — from flights and holidays to visas and corporate travel. Every service starts with a simple enquiry."
      />
      <Reveal delay={0.15}>
        <Link to="/services" data-testid="services-view-all-link"
          className="inline-flex items-center gap-2 rounded-full border border-ocean/20 px-5 py-2.5 text-sm font-semibold text-ocean transition-colors hover:border-turquoise hover:text-turquoise">
          View All Services <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>

    <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-6">
      {SERVICES.map((service, i) => {
        const Icon = service.icon;
        const featured = i === 0;
        return (
          <Reveal key={service.slug} delay={i * 0.06} className={`${BENTO_SPANS[i]}`}>
            <article
              data-testid="service-card"
              className={`group flex h-full flex-col justify-between rounded-3xl p-6 ring-1 ring-ocean/5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-teal-900/10 ${BENTO_STYLES[i]} ${featured ? "" : "shadow-sm"}`}
            >
              <div>
                <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${featured ? "bg-gold/20 text-gold" : "bg-turquoise/10 text-turquoise"}`}>
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className={`mt-4 font-display text-lg font-bold sm:text-xl ${featured ? "text-white" : "text-ink"}`}>
                  {service.title}
                </h3>
                <p className={`mt-2 text-sm leading-relaxed ${featured ? "text-white/70" : "text-mutedink"}`}>
                  {service.description}
                </p>
              </div>
              <a
                href={waLink(`Hi ATO, I would like to enquire about ${service.title}.`)}
                target="_blank" rel="noopener noreferrer"
                data-testid={`service-enquire-${service.slug}`}
                className={`mt-5 inline-flex w-fit items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                  featured ? "bg-coral text-white" : "bg-ocean text-white hover:bg-turquoise"
                }`}
              >
                Enquire Now <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </article>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const STYLE_ICONS = { "Family Holidays": Users, "Romantic Getaways": Heart, "Beach Escapes": Sun, "Adventure Trips": Mountain };

const TravelStyles = () => (
  <section className="bg-sandlight py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <SectionHeading
        eyebrow="Travel Your Way"
        title="Choose Your Travel Style"
        text="However you like to travel, we can help shape the trip around it."
      />
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {TRAVEL_STYLES.map((style, i) => {
          const Icon = STYLE_ICONS[style.title];
          return (
            <Reveal key={style.title} delay={i * 0.08}>
              <article data-testid={`style-card-${style.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative h-96 overflow-hidden rounded-3xl">
                <img src={style.image} alt={style.title} loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ocean/90 via-ocean/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <Icon className="h-6 w-6 text-gold" />
                  <h3 className="mt-2 font-display text-xl font-bold text-white">{style.title}</h3>
                  <p className="mt-1.5 text-sm text-white/75">{style.description}</p>
                  <a
                    href={waLink(`Hi ATO, I would like to enquire about ${style.title.toLowerCase()}.`)}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`style-enquire-${style.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm transition-colors hover:bg-coral"
                  >
                    Enquire <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const WHY_ITEMS = [
  { icon: Wallet, title: "Options for every budget",
    text: "Travel options across different needs and budgets — we help you compare and choose what fits." },
  { icon: HeartHandshake, title: "Personalised planning",
    text: "Holiday planning shaped around your dates, preferences and pace — not off-the-shelf itineraries." },
  { icon: Layers, title: "Every service in one place",
    text: "Flights, holidays, cruises, visas, insurance and corporate travel assistance under one roof." },
  { icon: MessagesSquare, title: "A convenient enquiry process",
    text: "Reach us on WhatsApp or through the enquiry form, and our team takes it from there." },
];

const WhyChooseUs = () => (
  <section className="bg-ocean py-20 text-white sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <SectionHeading dark eyebrow="Why ATO" title="Why Choose ARORA TRAVEL ONLINE?"
        text="We keep travel planning simple, personal and honest." />
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {WHY_ITEMS.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.08}>
            <div className="h-full rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-colors duration-300 hover:border-turquoise/50">
              <item.icon className="h-8 w-8 text-gold" />
              <h3 className="mt-4 font-display text-lg font-bold">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">{item.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [
  { number: "01", icon: Send, title: "Share Your Plans",
    text: "Tell us your destination, dates and requirements — on WhatsApp or through the enquiry form." },
  { number: "02", icon: Compass, title: "Explore Your Options",
    text: "Our team can discuss suitable available options with you, based on your plans and budget." },
  { number: "03", icon: MapPin, title: "Plan Your Getaway",
    text: "Connect with our team to finalise the details and take the next steps for your trip." },
];

const HowItWorks = () => (
  <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
    <SectionHeading align="center" eyebrow="Simple Process" title="How It Works"
      text="Three easy steps between you and your next journey." />
    <div className="mt-14 grid gap-10 md:grid-cols-3">
      {STEPS.map((step, i) => (
        <Reveal key={step.number} delay={i * 0.12} className="relative">
          <span className="outline-number font-display text-7xl font-extrabold">{step.number}</span>
          <div className="mt-2 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-turquoise/10 text-turquoise">
              <step.icon className="h-5 w-5" />
            </span>
            <h3 className="font-display text-xl font-bold text-ink">{step.title}</h3>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-mutedink">{step.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Reviews = () => (
  <section className="bg-sandlight py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <SectionHeading
        align="center"
        eyebrow="Testimonials"
        title="Happy Travellers, Happy Memories"
        text="Real experiences make every journey more special."
      />

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {REVIEWS.map((review, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <article
              data-testid={`review-card-${i + 1}`}
              className="relative flex h-full flex-col rounded-3xl bg-white p-7 shadow-sm ring-1 ring-ocean/5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-teal-900/10"
            >
              {review.sample && (
                <span className="absolute right-5 top-5 rounded-full bg-sand px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-ocean/60">
                  Sample
                </span>
              )}
              <div className="flex gap-1" aria-label={`${review.rating} star rating`}>
                {Array.from({ length: review.rating }).map((_, s) => (
                  <Star key={s} className="h-4 w-4 text-gold" fill="currentColor" />
                ))}
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-mutedink">
                &ldquo;{review.text}&rdquo;
              </p>
              <div className="mt-6 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-turquoise/10 font-display text-sm font-bold text-turquoise">
                  {review.name.split(" ").map((w) => w[0]).join("")}
                </span>
                <div>
                  <p className="text-sm font-bold text-ink">{review.name}</p>
                  <p className="text-xs font-semibold text-coral">{review.trip}</p>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12 text-center">
        <a
          href={waLink("Hi ATO, I would like to plan my trip.")}
          target="_blank" rel="noopener noreferrer"
          data-testid="reviews-plan-trip-button"
          className="inline-flex items-center gap-2 rounded-full bg-coral px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-coral/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B01A23]"
        >
          Plan Your Trip <Send className="h-4 w-4" />
        </a>
      </Reveal>
    </div>
  </section>
);

const EnquirySection = () => (
  <section id="enquiry" className="bg-sand py-20 sm:py-28">
    <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-2">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-turquoise">Get In Touch</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
          Let's Plan Your Next Journey!
        </h2>
        <p className="mt-4 max-w-md text-base leading-relaxed text-mutedink">
          Tell us what you have in mind, and our team will get in touch to
          discuss your travel requirements.
        </p>
        <div className="mt-8 space-y-3 text-sm text-ink/80">
          <p className="flex items-center gap-2.5">
            <MapPin className="h-4 w-4 text-coral" /> {SITE.address}
          </p>
          <p className="flex items-center gap-2.5">
            <MessagesSquare className="h-4 w-4 text-coral" /> WhatsApp: {SITE.whatsappDisplay}
          </p>
        </div>
        <a
          href={waLink(WA_DEFAULT_MSG)}
          target="_blank" rel="noopener noreferrer"
          data-testid="enquiry-section-whatsapp-button"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-900/20 transition-all duration-300 hover:-translate-y-0.5"
        >
          <MessagesSquare className="h-4 w-4" /> Chat on WhatsApp
        </a>
      </Reveal>
      <Reveal delay={0.15}>
        <EnquiryForm />
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />

      <section id="destinations" className="bg-sandlight py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow="Where To Next" title="Popular Destinations"
              text="A few of the places our travellers ask about most — in India and beyond." />
            <Reveal delay={0.15}>
              <Link to="/destinations" data-testid="destinations-view-all-link"
                className="inline-flex items-center gap-2 rounded-full border border-ocean/20 px-5 py-2.5 text-sm font-semibold text-ocean transition-colors hover:border-turquoise hover:text-turquoise">
                All Destinations <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {DESTINATIONS.map((destination, i) => (
              <Reveal key={destination.name} delay={(i % 3) * 0.08}>
                <DestinationCard destination={destination} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ServicesBento />
      <TravelStyles />
      <WhyChooseUs />
      <HowItWorks />
      <Reviews />
      <EnquirySection />
    </>
  );
}
